<template>
  <div>
    <div class="solution_sheet__header">
      <p class="t-h--1 solution_sheet__title">{{ calculation.Description }}</p>
      <div class="solution_sheet__header_actions">
        <b-button
          class="solution_sheet__header_action_btn"
          :loading="fetchingUsersCalculationNote"
          type="is-primary"
          @click="handleGetCalculationNoteClick"
          inverted
          >{{ $t("Nota de cálculo") }}</b-button
        >
        <b-button
          class="solution_sheet__header_action_btn"
          type="is-primary"
          tag="router-link"
          :to="{
            name: 'Calculator',
            query: { calculationId: calculation.id },
          }"
          >{{ $t("Voltar a simular") }}</b-button
        >
      </div>
    </div>

    <app-page-section :title="$t('Dados gerais')">
      <solution-sheet-item :title="$t('Tipo de simulação')">
        <p class="solution_sheet__item__val">
          {{ $t("input.type", calculation.input.type) }}
        </p>
      </solution-sheet-item>

      <solution-sheet-item :title="$t('Localização da obra')">
        <p class="solution_sheet__item__val">
          {{ calculation.input.country }}
        </p>
      </solution-sheet-item>
    </app-page-section>

    <app-page-section title="Dados geométricos">
      <solution-sheet-item :title="$t('Orientação do perfil')">
        <p class="solution_sheet__item__val">
          {{ $t("input.direction", calculation.input.direction) }}
        </p>
      </solution-sheet-item>

      <solution-sheet-item :title="$t('Formato')">
        <p class="solution_sheet__item__val">
          {{
            $t("Formato {format}", null, { format: calculation.input.format })
          }}
          <img
            class="format_img"
            :src="
              require(`@/assets/img/format_${calculation.input.format}.svg`)
            "
          />
        </p>
      </solution-sheet-item>

      <solution-sheet-item :title="$t('Número de vãos')">
        <p class="solution_sheet__item__val">
          {{ calculation.input.modelSpans.length }}
        </p>
      </solution-sheet-item>

      <solution-sheet-item-model-spans
        :model-spans="calculation.input.modelSpans"
        :main-spans="mainSpans"
        :cantilever-spans="cantileverSpans"
      />

      <solution-sheet-item :title="$t('Espaçamento entre perfis')">
        <p class="solution_sheet__item__val">
          {{ calculation.input.transversalDistance }}
        </p>
      </solution-sheet-item>

      <solution-sheet-item :title="$t('Largura de apoios')">
        <p class="solution_sheet__item__val">
          <template v-if="calculation.input.supportWidth === 1"
            >{{ $t("L. apoios") }} &lt; 135 mm</template
          >
          <template v-if="calculation.input.supportWidth === 2">
            {{ $t("L. apoios") }} &gt;=135 mm — {{ $t("L. apoios") }} &lt; 200
            mm
          </template>
          <template v-if="calculation.input.supportWidth === 3"
            >{{ $t("L. apoios") }}s &gt;= 200mm</template
          >
        </p>

        <template slot="illustration">
          <calc-illustration
            which="spans"
            :hasCantilever="calculation.input.hasCantilever"
            :totalSpans="calculation.input.modelSpans.length"
          />
        </template>
      </solution-sheet-item>
    </app-page-section>

    <app-page-section :title="$t('Cargas permanentes')" v-if="hasPermanentLoad">
      <solution-sheet-item-min-max-load
        v-if="loadValue('permanent', 'distributed_min')"
        :load-min-value="loadValue('permanent', 'distributed_min')"
        :load-max-value="loadValue('permanent', 'distributed_max')"
        :title="$t('Distribuída')"
        :has-cantilever="calculation.input.hasCantilever"
      />

      <solution-sheet-item-generic-load
        v-if="loadValue('permanent', 'distributed')"
        :load-value="loadValue('permanent', 'distributed')"
        :title="$t('Distribuída')"
        :has-cantilever="calculation.input.hasCantilever"
      />

      <solution-sheet-item-point-load
        :title="$t('Carga pontual')"
        v-if="pointLoadGroups('permanent')"
        :point-load-groups="pointLoadGroups('permanent')"
        :has-cantilever="calculation.input.hasCantilever"
        :model-spans-total="calculation.input.modelSpans.length"
      />
    </app-page-section>

    <app-page-section :title="$t('Sobrecarga')" v-if="hasLiveLoad">
      <solution-sheet-item-generic-load
        :load-value="loadValue('live', 'distributed')"
        :title="$t('Distribuída')"
        :has-cantilever="calculation.input.hasCantilever"
      />

      <solution-sheet-item-point-load
        :title="$t('Sobrecarga pontual')"
        v-if="pointLoadGroups('live')"
        :point-load-groups="pointLoadGroups('live')"
        :has-cantilever="calculation.input.hasCantilever"
        :model-spans-total="calculation.input.modelSpans.length"
      />
    </app-page-section>

    <app-page-section :title="$t('Vento')" v-if="hasWindLoads">
      <solution-sheet-item-wind-load
        :title="$t('Vento')"
        v-if="getLoadsByCase('wind').length"
        :pressure-load-value="loadValue('wind', 'pressure')"
        :suction-load-value="loadValue('wind', 'suction')"
        :is-by-band="calculation.input.internal.loads.isWindLoadsByBand"
      />

      <solution-sheet-item-wind-load
        :title="$t('Vento local')"
        v-if="getLoadsByCase('windLocal').length"
        :pressure-load-value="loadValue('windLocal', 'pressure')"
        :suction-load-value="loadValue('windLocal', 'suction')"
        :is-by-band="calculation.input.internal.loads.isWindLoadsByBand"
      />
    </app-page-section>

    <app-page-section
      :title="$t('Neve')"
      v-if="calculation.input.type !== 2 && hasSnowLoads"
    >
      <solution-sheet-item-generic-load
        v-if="loadValue('snow', 'regular')"
        :load-value="loadValue('snow', 'regular')"
        :title="$t('Carga')"
        :has-cantilever="calculation.input.hasCantilever"
      />

      <solution-sheet-item-generic-load
        v-if="loadValue('snow', 'exceptional')"
        :load-value="loadValue('snow', 'exceptional')"
        :title="$t('Carga excepcional')"
        :has-cantilever="calculation.input.hasCantilever"
      />

      <solution-sheet-item-snow-accumulation
        v-if="loadValue('snow', 'accumulation')"
        :load-value="loadValue('snow', 'accumulation')"
      />
    </app-page-section>

    <app-page-section :title="$t('Parâmetros adicionais')">
      <solution-sheet-item :title="$t('Esforço axial')" v-if="axialLoad">
        <solution-sheet-sub-item :title="$t('Carga axial de dimensionamento')">
          <p class="solution_sheet__item__val">{{ axialText }}</p>
        </solution-sheet-sub-item>
      </solution-sheet-item>

      <solution-sheet-item-span-restraints
        :span-restraints="calculation.input.spanRestraints"
      />

      <solution-sheet-item :title="$t('Deformação')">
        <solution-sheet-sub-item :title="$t('Limite')">
          <p class="solution_sheet__item__val">
            {{ calculation.input.deformation.limit }}
          </p>
        </solution-sheet-sub-item>
        <solution-sheet-sub-item :title="$t('Combinação')">
          <p class="solution_sheet__item__val">
            {{
              $t(
                "input.deformation.combination",
                calculation.input.deformation.combination
              )
            }}
          </p>
        </solution-sheet-sub-item>
      </solution-sheet-item>
    </app-page-section>

    <app-page-section v-if="selectedSolution" title="Resultado seleccionado">
      <solution-sheet-item :title="selectedSolution.name">
        <div class="columns">
          <div class="column">
            <solution-sheet-sub-item :title="$t('Aço')">
              <p class="solution_sheet__item__val">
                {{ selectedSolution.steel }}
              </p>
            </solution-sheet-sub-item>

            <solution-sheet-sub-item :title="$t('Altura (mm)')">
              <p class="solution_sheet__item__val">
                {{ selectedSolution.height }}
              </p>
            </solution-sheet-sub-item>

            <solution-sheet-sub-item :title="$t('Espessura (mm)')">
              <p class="solution_sheet__item__val">
                {{ selectedSolution.thickness }}
              </p>
            </solution-sheet-sub-item>
          </div>

          <div class="column">
            <solution-sheet-sub-item :title="$t('Comp Alinhamento (m)')">
              <p class="solution_sheet__item__val">
                {{ selectedSolution.lengthAlignment }}
              </p>
            </solution-sheet-sub-item>

            <solution-sheet-sub-item :title="$t('Peso Alinhamento (kg)')">
              <p class="solution_sheet__item__val">
                {{ selectedSolution.totalWeightAlignment }}
              </p>
            </solution-sheet-sub-item>

            <solution-sheet-sub-item :title="$t('Peso estimado (kg/m2)')">
              <p class="solution_sheet__item__val">
                {{ selectedSolution.totalWeightEstimated }}
              </p>
            </solution-sheet-sub-item>
          </div>
        </div>
      </solution-sheet-item>
    </app-page-section>

    <div class="u-df u-jcfe">
      <div class="o-horizontal_list">
        <b-button
          :loading="fetchingUsersCalculationNote"
          type="is-primary"
          @click="handleGetCalculationNoteClick"
          inverted
          >{{ $t("Nota de cálculo") }}</b-button
        >
        <b-button
          type="is-primary"
          tag="router-link"
          :to="{
            name: 'Calculator',
            query: { calculationId: calculation.id },
          }"
          >{{ $t("Voltar a simular") }}</b-button
        >
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { lastOf } from "@/utils";

import {
  SolutionSheetItemGenericLoad,
  SolutionSheetItemMinMaxLoad,
  SolutionSheetItemWindLoad,
  SolutionSheetItemModelSpans,
  SolutionSheetItemPointLoad,
  SolutionSheetItemSnowAccumulation,
  SolutionSheetItemSpanRestraints,
  SolutionSheetSubItem,
  SolutionSheetItem,
  CalcIllustration,
} from "@/components";

export default {
  components: {
    SolutionSheetItemGenericLoad,
    SolutionSheetItemMinMaxLoad,
    SolutionSheetItemWindLoad,
    SolutionSheetItemModelSpans,
    SolutionSheetItemPointLoad,
    SolutionSheetItemSnowAccumulation,
    SolutionSheetItemSpanRestraints,
    SolutionSheetSubItem,
    SolutionSheetItem,
    CalcIllustration,
  },
  props: {
    calculation: {
      required: true,
    },
  },

  data() {
    return {
      fetchingUsersCalculationNote: false,
      downloadPdfLink: undefined,
    };
  },

  computed: {
    ...mapState(["usersCalculationsNotes", "selectedLang"]),

    calculationNote() {
      return this.usersCalculationsNotes.find(
        ({ id }) => id === this.calculation.id
      );
    },

    loadValue() {
      return (_case, _type) => {
        const loadObj = this.calculation.input.loads.find((load) => {
          return _case === load.case && _type === load.type;
        });

        return loadObj ? loadObj.value : null;
      };
    },

    pointLoadGroups() {
      return (_case) => {
        const pointLoads = this.calculation.input.internal.loads["points"];
        const groups = pointLoads && pointLoads[_case];

        if (groups) {
          const groupsWithValuesToShow = groups.filter(({ value }) => {
            return value[0] !== 0 || value[2] !== 0;
          });

          if (groupsWithValuesToShow.length) {
            return groupsWithValuesToShow;
          }
        }

        return null;
      };
    },

    mainSpans() {
      const { input } = this.calculation;

      if (input.hasCantilever) {
        return input.modelSpans.slice(1, -1);
      } else {
        return [...input.modelSpans];
      }
    },

    cantileverSpans() {
      const { input } = this.calculation;

      if (input.hasCantilever) {
        return [input.modelSpans[0], lastOf(input.modelSpans)];
      } else {
        return null;
      }
    },

    selectedSolution() {
      const id = this.calculation.chosenSolution;
      return this.calculation.output.solutions.find(
        (solution) => solution.id === id
      );
    },

    hasSnowLoads() {
      return !!this.getLoadsByCase("snow").length;
    },

    hasWindLoads() {
      return !!(
        this.getLoadsByCase("wind").length ||
        this.getLoadsByCase("windLocal").length
      );
    },

    hasPermanentLoad() {
      return (
        this.pointLoadGroups("permanent") ||
        this.loadValue("permanent", "distributed") ||
        this.loadValue("permanent", "distributed_min")
      );
    },

    hasLiveLoad() {
      return (
        this.pointLoadGroups("live") || this.loadValue("live", "distributed")
      );
    },

    axialLoad() {
      return this.getLoadsByCase("axial")[0];
    },

    axialText() {
      const { axialLoad } = this;

      let text;

      if (axialLoad.type === "notMajored") {
        text =
          this.$t("Carga axial não majorada devido à acção do vento") + ": ";
      } else if (axialLoad.type === "axialRegular") {
        text = axialLoad.value + "kN";
      }

      return text;
    },
  },

  methods: {
    ...mapActions(["fetchUsersCalculationNote"]),

    getLoadsByCase(_case) {
      return this.calculation.input.loads.filter((load) => {
        return _case === load.case;
      });
    },

    async handleGetCalculationNoteClick() {
      let fetchPromise;

      if (!this.calculationNote) {
        this.fetchingUsersCalculationNote = true;

        fetchPromise = this.fetchUsersCalculationNote({
          id: this.calculation.id,
          type: this.calculation.input.type,
          lang: this.selectedLang,
        }).finally(() => {
          this.fetchingUsersCalculationNote = false;
        });
      } else {
        fetchPromise = Promise.resolve();
      }

      fetchPromise.then(() => {
        if (!this.downloadPdfLink) {
          this.createDownloadPdfLink();
        }

        this.triggerPdfDownload();
      });
    },

    createDownloadPdfLink() {
      this.downloadPdfLink = document.createElement("a");
      this.downloadPdfLink.href = `data:application/octet-stream;base64,${this.calculationNote.pdfFile}`;
      this.downloadPdfLink.download = `calculo-${this.calculation.Description}.pdf`;
    },

    triggerPdfDownload() {
      this.downloadPdfLink.click();
    },
  },
};
</script>

<style lang="scss" scoped>
.solution_sheet__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0 8px;
}

.solution_sheet__header_actions {
  margin-top: 0.55em;
  white-space: nowrap;
}

.solution_sheet__header_action_btn {
  margin-left: 1em;
}

.format_img {
  vertical-align: text-top;
}

::v-deep .illustration {
  margin-bottom: -13px;
}

@media screen and (max-width: 725px) {
  ::v-deep .solution_sheet__item--has_illustration,
  ::v-deep .solution_sheet__item__subitem--has_illustration {
    display: block;
  }

  ::v-deep .solution_sheet__item--has_illustration:not(:last-child),
  ::v-deep .solution_sheet__item__subitem--has_illustration:not(:last-child) {
    margin-bottom: 40px;
  }

  ::v-deep .solution_sheet__item__illustration,
  ::v-deep .solution_sheet__item__subitem__illustration {
    margin-left: -2px;
    margin-top: 15px;
  }
}

@media screen and (max-width: 690px) {
  .solution_sheet__header {
    display: block;
  }

  .solution_sheet__header_actions {
    margin-top: 1em;
    margin-bottom: 1em;
  }

  .solution_sheet__header_action_btn {
    margin-right: 1em;
    margin-left: 0;
  }
}
</style>
